import React, { useEffect } from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
    getRoundedCurrentDatetime,
    selectOnlineDateOverrideValue,
    setOnlineDateOverrideUncommittedValue,
    submitDeviceDatasetOnlineDateOverride,
    selectOnlineDateOverrideCommittedValue,
    selectDeviceData,
    loadLatestDeviceDataDataset,
    selectOnlineDateOverrideStatus,
    selectDeviceDataDatasetStatus,
    selectOnlineDateOverrideCommitWillChangeValue,
    selectCurrentClient,
    selectCurrentProject,
    resetOnlineDateOverride,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    CircularProgress,
    InputLabel,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

dayjs.extend(utc);

const useStyles = makeStyles({
    component: {},
    formContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "end",
    },
    modal: {
        width: "40em",
        backgroundColor: "white",
        border: "2px solid #000",
        margin: "auto",
        marginTop: "50px",
        padding: "20px",
    },
});

export default function OnlineDateOverrideInput(props: { className: string }) {
    const dispatch = useAppDispatch();
    const classes = useStyles();

    const deviceDataset = useAppSelector(selectDeviceData);
    const onlineDateOverride = useAppSelector(selectOnlineDateOverrideValue);
    const onlineDateOverrideSetStatus = useAppSelector(selectOnlineDateOverrideStatus);
    const deviceDataDatasetStatus = useAppSelector(selectDeviceDataDatasetStatus);
    const onlineDateOverrideCommittedValue = useAppSelector(selectOnlineDateOverrideCommittedValue);
    const onlineDateOverrideWillChangeValue = useAppSelector(selectOnlineDateOverrideCommitWillChangeValue);

    const clientName = useAppSelector(selectCurrentClient);
    const projectName = useAppSelector(selectCurrentProject);

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    useEffect(() => {
        if (!modalIsOpen) {
            // Clear out online date override on modal close
            dispatch(resetOnlineDateOverride());
        }
    }, [modalIsOpen]);

    const isLoading = !(deviceDataDatasetStatus !== "loading" && onlineDateOverrideSetStatus !== "loading");

    return (
        <>
            <div className={props.className}>
                <Button
                    disabled={deviceDataset === undefined}
                    onClick={() => {
                        setModalIsOpen(true);
                    }}
                >
                    Add Online Date Override
                </Button>
                {deviceDataset !== undefined && (
                    <InputLabel>
                        Current Override Value: <b>{onlineDateOverrideCommittedValue || "Unset"}</b>
                    </InputLabel>
                )}
            </div>
            <Dialog
                open={modalIsOpen}
                onClose={() => setModalIsOpen(false)}
                aria-labelledby="online-date-override-modal-title"
                aria-describedby="online-date-override-modal-description"
            >
                <DialogTitle
                    id="online-date-override-modal-title"
                    sx={{ display: "flex", alignItems: "center", mb: 0 }}
                >
                    <h2 style={{ flex: "1", marginBottom: 0 }}>Online Date Override</h2>
                    <IconButton>
                        <Close onClick={() => setModalIsOpen(false)} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <p style={{ marginTop: 0 }}>
                        <i>
                            Use this dialog to set/unset the online date override value for the project {clientName} /{" "}
                            {projectName}.
                        </i>
                    </p>
                    <p>
                        Typically, the latest device install date/time is used to determine the online and SRB
                        date/times for every site group in the project. However, if you want to override the value
                        assigned to the project-wide <pre style={{ display: "inline" }}>site_group.online_date</pre> and{" "}
                        <pre style={{ display: "inline" }}>site_group.srb_online_date</pre> columns, you can do so here.
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        {deviceDataset !== undefined && (
                            <InputLabel>
                                Current Value: <b>{onlineDateOverrideCommittedValue || "Unset"}</b>
                            </InputLabel>
                        )}
                        {onlineDateOverride && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label="Install Date Override (in UTC)"
                                    value={dayjs.utc(onlineDateOverride)}
                                    onChange={(newDate) =>
                                        dispatch(setOnlineDateOverrideUncommittedValue(newDate?.toISOString()))
                                    }
                                    disabled={isLoading}
                                />
                            </LocalizationProvider>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    {onlineDateOverride ? (
                        <Button
                            variant="text"
                            onClick={() => {
                                dispatch(setOnlineDateOverrideUncommittedValue(undefined));
                            }}
                            disabled={isLoading}
                        >
                            Clear Value
                        </Button>
                    ) : (
                        <Button
                            variant="text"
                            disabled={isLoading}
                            onClick={() => {
                                dispatch(
                                    setOnlineDateOverrideUncommittedValue(
                                        onlineDateOverrideCommittedValue || getRoundedCurrentDatetime().toISOString()
                                    )
                                );
                            }}
                        >
                            Add Value
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        disabled={!onlineDateOverrideWillChangeValue || isLoading}
                        onClick={async () => {
                            await dispatch(submitDeviceDatasetOnlineDateOverride());
                            await dispatch(loadLatestDeviceDataDataset());
                            setModalIsOpen(false);
                        }}
                    >
                        Submit Change {isLoading && <CircularProgress size={20} />}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
